import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CLAUDE_MODEL_IDS, type DepartmentDTO } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { PageHeader, Panel, toneColor, type Tone } from "@/design/primitives";
import { useAuth } from "@/auth/auth";

/** Globální registr oddělení (spec §7, /Design DEPARTMENTS). */
export function DepartmentsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const query = useQuery({ queryKey: ["departments"], queryFn: api.departments });
  const isOwner = session?.user.role === "OWNER";

  const toggleMutation = useMutation({
    mutationFn: (d: DepartmentDTO) => api.updateDepartment(d.id, { enabled: !d.enabled }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["departments"] }),
  });

  const modelMutation = useMutation({
    mutationFn: (input: { id: string; modelHint: string | null }) =>
      api.updateDepartment(input.id, { modelHint: input.modelHint }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["departments"] }),
  });

  const modelLabel = (id: string) => (cs.models as Record<string, string>)[id] ?? id;

  const departments = query.data?.departments ?? [];

  return (
    <div className="af-animate-fade">
      <PageHeader
        title={cs.departments.title}
        kicker="REGISTR"
        lead={cs.departments.subtitle}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {departments.map((d) => (
          <Panel
            key={d.id}
            className="flex cursor-pointer flex-col gap-2 p-4"
            style={{ opacity: d.enabled ? 1 : 0.45 }}
            onClick={() => void navigate({ to: "/departments/$key", params: { key: d.key } })}
            data-testid={`department-${d.key}`}
          >
            <div className="flex items-center justify-between">
              <span className="af-card-title">
                <span style={{ color: toneColor[(d.color as Tone) ?? "cyan"] }}>■</span> {d.name}
              </span>
              <button
                type="button"
                className="af-mono cursor-pointer px-2.5 py-[3px] text-[10px] font-bold"
                style={{
                  borderRadius: "var(--radius-pill)",
                  background: d.enabled ? "var(--positive-bg)" : "var(--surface-inset)",
                  color: d.enabled ? "var(--positive-on-tint)" : "var(--text-tertiary)",
                }}
                disabled={!isOwner}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isOwner) toggleMutation.mutate(d);
                }}
                title={isOwner ? "Kliknutím přepneš aktivaci" : undefined}
              >
                {d.enabled ? cs.departments.active : cs.departments.disabled}
              </button>
            </div>
            <div className="text-[11px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              {d.description}
            </div>
            {d.basePrompt ? (
              <div
                className="pl-2 text-[11px] italic leading-relaxed"
                style={{ color: "var(--text-tertiary)", borderLeft: "2px solid color-mix(in srgb, var(--accent) 15%, transparent)" }}
              >
                „{d.basePrompt.slice(0, 90)}…“
              </div>
            ) : null}
            <div className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
              <span style={{ color: "var(--text-tertiary)" }}>{cs.departments.workflow}</span>{" "}
              {d.workflowKey.toUpperCase()}
            </div>
            <div className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
              <span style={{ color: "var(--text-tertiary)" }}>{cs.departments.tools}</span>{" "}
              {d.capabilities.join(" · ") || "—"}
            </div>
            <div
              className="flex items-center gap-1.5 text-[11px]"
              style={{ color: "var(--text-secondary)" }}
              data-testid={`department-model-${d.key}`}
            >
              <span style={{ color: "var(--text-tertiary)" }}>{cs.departments.model}</span>
              {isOwner ? (
                <select
                  className="min-w-0 flex-1 cursor-pointer px-1 py-0.5 text-[11px] outline-none"
                  style={{ borderRadius: "var(--radius-box)",
                    background: "var(--surface-inset)",
                    border: "1px solid var(--border-default)",
                    color: d.modelHint ? "var(--text-primary)" : "var(--text-secondary)",
                  }}
                  title={cs.departments.modelTitle}
                  value={d.modelHint ?? ""}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    e.stopPropagation();
                    modelMutation.mutate({ id: d.id, modelHint: e.target.value || null });
                  }}
                >
                  <option value="">{cs.departments.modelDefault}</option>
                  {d.modelHint && !(CLAUDE_MODEL_IDS as readonly string[]).includes(d.modelHint) ? (
                    <option value={d.modelHint}>{d.modelHint}</option>
                  ) : null}
                  {CLAUDE_MODEL_IDS.map((id) => (
                    <option key={id} value={id}>
                      {modelLabel(id)}
                    </option>
                  ))}
                </select>
              ) : (
                <span>{d.modelHint ? modelLabel(d.modelHint) : cs.departments.modelDefault}</span>
              )}
            </div>
            <div className="pt-1.5 text-[11px]" style={{ borderTop: "1px solid color-mix(in srgb, var(--accent) 8%, transparent)", color: "var(--text-tertiary)" }}>
              {cs.departments.stats(d.projectCount, d.openTaskCount)}
            </div>
          </Panel>
        ))}
      </div>
    </div>
  );
}
